import {
  Ng1StateDeclaration,
  StateParams,
  StateProvider,
} from "@uirouter/angularjs";

import { vsComponentName as processComponentName } from "../views/ProcessGroupGrid/component";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../records/grids/settings/gridSettingsService";
import {
  ProcessInformationService,
  vsServiceName as ProcessInformationServiceName,
  ProcessInformationServiceProvider,
} from "../services/processInformationService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import { ProcessGroup, ProcessInformation } from "../model/ProcessGroups";
import { ServiceResponse } from "../../records/server/query";
import { ProcessGroupConfig } from "./ProcessGroupConfig";
import {
  AuthorizedUiElementConfig,
  AuthorizedUiElementServiceProvider,
} from "../../ui/authorizedUiElement";
import { IPromise, Injectable } from "angular";
import { ColDef } from "../../records/grids/columns/colDef";

export function configureProcessGroup(
  config: ProcessGroupConfig,
  $stateProvider: StateProvider,
  processInformationServiceProvider: ProcessInformationServiceProvider
) {
  processInformationServiceProvider.addRuntimeConfig(config);
  const baseSref = config.baseSref;
  $stateProvider
    .state(
      baseSref,
      getProcessState(
        config.processGroupId,
        nullSymbol,
        ProcessInformationService.defaultDefaultQuery,
        [
          ProcessInformationServiceName,
          "processGroup",
          (
            processInformationService: ProcessInformationService,
            processGroup: ProcessGroup
          ) => [
            "process",
            (process: ProcessInformation) =>
              processInformationService
                .createNewProcessFlow(processGroup, process)
                .then((processResponse) =>
                  processInformationService.navigateToDetail(processResponse)
                ),
          ],
        ],
        config.additionalGridColumns ??
          ProcessInformationService.defaultAdditionalGridColumns
      )
    )
    .state(
      `${baseSref}.detail`,
      getProcessDetailState(resolveByProcessGroupId(config.processGroupId))
    );
}

export function configureDetailView<
  TDetailTabProvider extends AuthorizedUiElementServiceProvider<AuthorizedUiElementConfig>
>(
  config: ProcessGroupConfig,
  detailTabsProvider: TDetailTabProvider,
  defaultQueryFactory: Injectable<(...args: any[]) => any>,
  onNewProcessSelected: Injectable<
    (
      ...args: any[]
    ) => Injectable<(process: ProcessInformation) => IPromise<any>>
  >
) {
  detailTabsProvider.addElement({
    id: "Process",
    displayName: "Process",
    uiSrefFragment: "process",
    stateDefinition: getProcessState(
      config.processGroupId,
      Symbol(`${detailTabsProvider.basePath}.process.view`),
      defaultQueryFactory,
      onNewProcessSelected,
      ProcessInformationService.defaultAdditionalGridColumns
    ),
    children: [
      {
        id: "detail",
        displayName: "Detail",
        uiSrefFragment: "detail",
        stateDefinition: getProcessDetailState(
          resolveByProcessGroupId(config.processGroupId)
        ),
      },
    ],
  });
}

function resolveByProcessGroupId(processGroupId: string) {
  return [
    ProcessInformationServiceName,
    (processInformationService: ProcessInformationService) => {
      return processInformationService.getDetailTemplate(processGroupId);
    },
  ];
}

export function getProcessDetailState(
  templateProviderResolver: Injectable<(...args: any[]) => string>
): Ng1StateDeclaration {
  return {
    url: "/detail/{processInstancesId:int}",
    templateProvider: templateProviderResolver,
    controller: [
      "$scope",
      "model",
      "tree",
      "processInformation",
      "processGroup",
      (
        $scope: any,
        model: any,
        tree: any,
        processInformation: any,
        processGroup: any
      ) => {
        $scope.model = model;
        $scope.tree = tree;
        $scope.processInformation = processInformation;
        $scope.processGroup = processGroup;
      },
    ],
    resolve: {
      model: [
        ServerUtilityName,
        "$stateParams",
        "processGroup",
        function (
          serverUtility: ServerUtility,
          $stateParams: StateParams,
          processGroup: ProcessGroup
        ) {
          const processInstancesId = $stateParams["processInstancesId"];
          return serverUtility
            .sendApiRequest({
              method: "get",
              url: `/api${processGroup.Url}?processInstancesId=${processInstancesId}`,
            })
            .then((r) => (<ServiceResponse<unknown[]>>r).Results.at(0));
        },
      ],
      tree: [
        ServerUtilityName,
        "$stateParams",
        "processGroup",
        function (
          serverUtility: ServerUtility,
          $stateParams: StateParams,
          processGroup: ProcessGroup
        ) {
          const processInstancesId = $stateParams["processInstancesId"];
          return serverUtility
            .sendApiRequest({
              method: "get",
              url: `/api${processGroup.Url}/${processInstancesId}`,
            })
            .then((r) => (<ServiceResponse<unknown>>r).Result);
        },
      ],
      processInformation: [
        "processGroup",
        "model",
        (processGroup: ProcessGroup, model: { Type: string }) =>
          processGroup.ProcessInformation.find((p) => p.Type === model.Type),
      ],
    },
  };
}

export const nullSymbol = Symbol("onlyUsedForEqualityCheckToFallBackToDefault");
export function getProcessState(
  groupId: string,
  gridSettingsName: symbol,
  defaultQueryFactory: Injectable<(...args: any[]) => any>,
  onNewProcessSelected: Injectable<
    (
      ...args: any[]
    ) => Injectable<(process: ProcessInformation) => IPromise<any>>
  >,
  additionalGridColumns: Injectable<(...args: any[]) => ColDef[]>
): Ng1StateDeclaration {
  return {
    url: "/process",
    component: processComponentName,
    resolve: {
      processGroup: [
        ProcessInformationServiceName,
        (processInformationService: ProcessInformationService) => {
          return processInformationService.getProcessGroup(groupId);
        },
      ],
      gridSettings: [
        GridSettingsServiceName,
        ProcessInformationServiceName,
        "processGroup",
        (
          gridSettingsService: GridSettingsService,
          processInformationService: ProcessInformationService,
          processGroup: ProcessGroup
        ) => {
          return gridSettingsName === nullSymbol
            ? processInformationService.getGridSettings(processGroup)
            : gridSettingsService.getGridSettings(gridSettingsName);
        },
      ],
      onNewProcessSelected,
      additionalGridColumns,
      defaultQuery: defaultQueryFactory,
    },
  };
}
